/**
 * Document360Page — Vista 360 de un documento de venta (Fase 2a).
 *
 * Ruta: /sales/documents/:id/360 (detrás del flag ui.sales_360 en el cutover).
 * Reusa el shell Workspace360 + MoneyTotals + RelatedTabsRail, el hook
 * useSaleDocument y el patrón blob+JWT para PDF/XML.
 *
 * Fase 2a: shell + rail + header + chips + centro conmutable PDF/Detalle/XML.
 * El panel contextual trae el rail de tabs y la pestaña "Cliente y origen"
 * cableada al endpoint de Fase 1; el resto de tabs se completa en Fase 2b.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { DataTable } from '@/components/ds/DataTable'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FileText, Loader2, Download, FileCode2, FilePenLine, ChevronLeft, ArrowLeft,
  User, Activity, Link2, BookOpen, DollarSign, Handshake, Paperclip, History,
  Copy, Check, Save, Mail, Send, Star, Pencil,
} from 'lucide-react'
import { usePermission } from '@/hooks/usePermission'
import { useMe, useUpdateUiPreference } from '@/modules/auth/hooks/useAuth'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { fmtDate, formatRut } from '@/lib/utils'
import apiClient from '@/lib/apiClient'
import { formatXml } from '@/components/XmlViewer'
import { ToastContainer, useToast } from '@/components/Toast'
import { useLocalizationConfig, useGlobalCustomFields, useCurrencyRates, useTenantTax } from '@/modules/config/hooks/useConfig'
import { useSaleDocument, useSaleDocuments, useCustomer, useCustomers, useProducts, useCloneDocument, useEmitDocument, useAdjustDocument, useUpdateDocumentMetadata, type SaleDocument, type SaleDocumentListItem, type DocumentPersonalizado } from '@/modules/sales/hooks/useSales'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import CustomFieldsForm, { mergeCustomFields } from '@/components/CustomFieldsForm'
import {
  Workspace360, MoneyTotals, buildDocMoneyStats, useClpFormatter, formatExchangeRate,
  RelatedTabsRail, type RelatedTab,
} from '@/components/three60'
import { DocumentFormPage, type DocumentFormTotals } from '@/modules/sales/components/DocumentFormPage'
import { DocumentModal } from '@/modules/sales/components/DocumentModal'
import { SendEmailModal } from '@/modules/sales/components/SendEmailModal'
import { PaymentLinkModal } from '@/modules/sales/components/PaymentLinkModal'
import { CustomerContextTab } from './tabs/CustomerContextTab'
import {
  SiiTimelineTab, RelatedTab as RelatedDocsTab, VoucherTab, CollectionTab, FactoringTab, AttachmentsTab, HistoryTab,
} from './tabs/PanelTabs'
import type { CollectionResponse } from './tabs/use360Queries'
import { ProductQuickView, CustomerQuickView, VoucherQuickView, PaymentQuickView } from './quickviews/QuickViews'
import { StatusBadge } from '@/components/ds/StatusBadge'

// Estado del quick-view drawer (entidad relacionada abierta en panel lateral).
type QuickState =
  | { type: 'product'; productId: string; name: string }
  | { type: 'customer'; customerId: string }
  | { type: 'voucher'; docId: string }
  | { type: 'payment'; payment: CollectionResponse['payments'][number] }

// ─── Helpers de estilo por tipo / estado ──────────────────────────────────────

// El estado del DTE sale del catálogo único (`StatusBadge` + `DTE_STATUS`). Acá
// había un quinto mapa propio que pintaba todo lo intermedio en azul, mientras
// el listado lo pinta en el tono `info` del catálogo (2026-09-30).

type CenterView = 'pdf' | 'detail' | 'xml'

const CENTER_LABEL: Record<CenterView, string> = { pdf: 'PDF', detail: 'Detalle', xml: 'XML' }

const REL_TABS: RelatedTab[] = [
  { key: 'customer', icon: <User size={18} />, title: 'Cliente y origen' },
  { key: 'sii', icon: <Activity size={18} />, title: 'Estado SII y trazabilidad' },
  { key: 'related', icon: <Link2 size={18} />, title: 'Relacionados (NC / ND)' },
  { key: 'voucher', icon: <BookOpen size={18} />, title: 'Comprobante contable' },
  { key: 'collection', icon: <DollarSign size={18} />, title: 'Cobranza' },
  { key: 'factoring', icon: <Handshake size={18} />, title: 'Factoring / Cesión' },
  { key: 'files', icon: <Paperclip size={18} />, title: 'Adjuntos' },
  { key: 'history', icon: <History size={18} />, title: 'Historial' },
]

export function Document360Page() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const locale = useTenantLocale()
  const fmt = useClpFormatter()
  const { can } = usePermission()

  const { data: doc, isLoading, isError } = useSaleDocument(id ?? null, { keepPrevious: true })
  // Rail paginado: arranca en 25 y crece de a 25 con el botón "Cargar más".
  // Crecemos el page_size (en vez de acumular páginas) para mantener una sola
  // lista ordenada sin lógica de dedup; los tamaños son chicos.
  const [railSize, setRailSize] = useState(25)
  // Contexto de listado según el tipo de documento: una Guía de Despacho (GD) vive
  // en /sales/guides (no se contabiliza), el resto en /sales/documents. De aquí salen
  // el "volver", la ruta del rail y de qué clave de sessionStorage lee sus filtros.
  const isGuide = doc?.document_type === 'GD'
  const listPath = isGuide ? '/sales/guides' : '/sales/documents'
  const railKey = isGuide ? 'sales-guides:rail-params' : 'sales-docs:rail-params'
  // El rail reusa los filtros del listado de origen (persistidos en sessionStorage)
  // para mostrar el MISMO conjunto que el usuario venía viendo —p. ej. tras buscar
  // "CMPC"—. Para una GD además fuerza type=GD (el listado normal las excluye).
  // Fallback a '-issue_date' si se entra directo por deep-link.
  const railParams = useMemo<Record<string, string>>(() => {
    let base: Record<string, string>
    try {
      const raw = sessionStorage.getItem(railKey)
      const parsed = raw ? (JSON.parse(raw) as Record<string, string>) : null
      base = parsed && Object.keys(parsed).length ? parsed : { ordering: '-issue_date' }
    } catch {
      base = { ordering: '-issue_date' }
    }
    return isGuide ? { ...base, type: 'GD' } : base
  }, [railKey, isGuide])
  const { data: listData, isFetching: isFetchingList } = useSaleDocuments({ ...railParams, page_size: railSize }, { keepPrevious: true })
  const { data: locConfig } = useLocalizationConfig()
  const tax = useTenantTax()
  const { data: me } = useMe()
  const updatePref = useUpdateUiPreference()
  // Vista predeterminada del centro, a nivel de perfil (persiste entre dispositivos).
  const prefView = me?.ui_preferences?.sales_360_default_view

  const [centerView, setCenterView] = useState<CenterView>('detail')
  const [activeTab, setActiveTab] = useState('customer')
  const [quick, setQuick] = useState<QuickState | null>(null)
  const [liveTotals, setLiveTotals] = useState<DocumentFormTotals | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)
  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false)
  const toast = useToast()
  const goDoc = (docId: string) => navigate(`/sales/documents/${docId}/360`)

  const cloneDoc = useCloneDocument()
  const emitDoc = useEmitDocument()

  // ── Ajuste NC/ND in-place (sin salir de la 360) ──
  // Reusa el mismo DocumentModal del listado, pero se monta SOBRE la 360 y, al
  // crear la nota, navega a la 360 de la nota recién creada (opción 1).
  const adjustDocMut = useAdjustDocument()
  const { confirm: confirmAction, modalProps: confirmProps } = useConfirmModal()
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [adjustInitial, setAdjustInitial] = useState<Partial<SaleDocument> | null>(null)
  const [adjustTargetId, setAdjustTargetId] = useState<string | null>(null)
  // Datos que el modal de ajuste necesita (catálogos + tasas).
  const { data: customersData } = useCustomers({ active: 'true', page_size: 9999 })
  const { data: globalFields = [] } = useGlobalCustomFields(true)
  const { data: currencyRatesData } = useCurrencyRates()

  const openAdjustForm = () => {
    if (!doc) return
    const TIPO_DOC: Record<string, string> = {
      FE: '33', FEE: '34', BE: '39', BEE: '41',
      FC: '46', ND: '56', NC: '61', FX: '110', FXE: '111', FXA: '112',
    }
    const tipoDoc = TIPO_DOC[doc.document_type] ?? '33'
    const folioRef = String((doc.localization_data as { folio?: unknown })?.folio ?? '')
    const referencia = {
      tipo_doc: tipoDoc,
      folio_ref: folioRef,
      fecha_ref: doc.issue_date,
      cod_ref: '1',
      razon_ref: `Anula ${doc.document_type} ${folioRef}`.trim(),
    }
    // Hereda referencias del original (filtra vacías del seed viejo) y antepone
    // el "Anula" auto-generado; pasa la tasa bloqueada para no recotizar.
    const originalRefs = (doc.referencias || []).filter(
      (r) => r && (r.folio_ref || r.razon_ref),
    )
    setAdjustTargetId(doc.id)
    setAdjustInitial({
      customer: doc.customer,
      document_type: 'NC',
      reference_document: doc.id,
      payment_method: doc.payment_method,
      notes: doc.notes,
      lines: doc.lines,
      referencias: [referencia, ...originalRefs],
      personalizados: doc.personalizados,
      exchange_rate_data: doc.exchange_rate_data,
    } as Partial<SaleDocument>)
    setAdjustOpen(true)
  }

  const handleAdjust = () => {
    if (!doc) return
    // Si la FE ya fue pagada (sin saldo), avisamos antes de abrir el form.
    const outstanding = parseFloat(doc.outstanding ?? '0')
    const total = parseFloat(doc.total ?? '0')
    if (outstanding <= 0 && total > 0) {
      const paidAmount = total - outstanding
      const fmtCLP = (v: number) => formatMoneyForTenant(v)
      confirmAction({
        message:
          `Esta factura ya fue pagada (${fmtCLP(paidAmount)}) y no tiene saldo pendiente. ` +
          `Si emites esta Nota de Crédito, se generará un crédito de ${fmtCLP(total)} a favor del cliente. ` +
          `Las políticas de reembolso se habilitarán en una versión futura. ¿Deseas continuar?`,
        variant: 'warning',
        confirmLabel: 'Continuar',
        onConfirm: openAdjustForm,
      })
    } else {
      openAdjustForm()
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleAdjustSubmit = (formData: any) => {
    if (!adjustTargetId) return
    const { due_date: _omit, ...data } = formData  // due_date lo calcula el backend
    adjustDocMut.mutate(
      { id: adjustTargetId, data: data as Partial<SaleDocument> },
      {
        onSuccess: (newDoc: { id: string }) => {
          setAdjustOpen(false)
          setAdjustTargetId(null)
          toast.success('Nota de ajuste creada.')
          goDoc(newDoc.id)  // opción 1: ir a la 360 de la nota recién creada
        },
        onError: () => toast.error('No se pudo crear la nota de ajuste.'),
      },
    )
  }

  const handleClone = () => {
    if (!doc) return
    cloneDoc.mutate(doc.id, {
      onSuccess: (created: { id: string }) => { toast.success('Documento clonado.'); goDoc(created.id) },
      onError: () => toast.error('No se pudo clonar el documento.'),
    })
  }
  const handleEmit = () => {
    if (!doc) return
    emitDoc.mutate(doc.id, {
      onSuccess: () => toast.success('Emisión iniciada.'),
      onError: () => toast.error('No se pudo emitir el documento.'),
    })
  }
  const handleDistribute = async () => {
    if (!doc) return
    // 1. Resolver (preview) el correo del receptor con la misma cascada del backend
    //    (padrón SII → CorreoRecep del XML → email del cliente).
    let recipient = ''
    let source = ''
    let already = false
    try {
      const res = await apiClient.get<{ recipient: string; source: string; already_distributed: boolean }>(
        `/sales/documents/${doc.id}/distribute-to-receiver/`,
      )
      recipient = res.data?.recipient || ''
      source = res.data?.source || ''
      already = !!res.data?.already_distributed
    } catch {
      toast.error('No se pudo resolver el correo del receptor.')
      return
    }
    if (!recipient) {
      toast.error('No hay un correo del receptor disponible. Configura el email del cliente o el CorreoRecep del XML.')
      return
    }
    // 2. Confirmar mostrando a qué correo (y de qué fuente) se enviará.
    const SOURCE_LABEL: Record<string, string> = {
      // El correo principal es el del padrón de Contribuyentes Electrónicos (CE)
      // que se descarga del SII; los otros dos son respaldos.
      ce_lookup: 'padrón CE del SII',
      correo_recep_xml: 'CorreoRecep del XML',
      customer_email: 'email del cliente',
    }
    const src = SOURCE_LABEL[source] ? ` (origen: ${SOURCE_LABEL[source]})` : ''
    confirmAction({
      message:
        `Se enviará el DTE al correo del receptor ${recipient}${src}.` +
        (already ? ' Este documento ya fue distribuido antes; se reenviará.' : ''),
      variant: already ? 'warning' : 'success',
      confirmLabel: already ? 'Reenviar' : 'Enviar',
      onConfirm: async () => {
        try {
          await apiClient.post(`/sales/documents/${doc.id}/distribute-to-receiver/`, { force: true })
          toast.success(`DTE distribuido a ${recipient}.`)
        } catch {
          toast.error('No se pudo distribuir el documento.')
        }
      },
    })
  }

  const tabsWithCounts = useMemo(() => {
    const counts: Record<string, number> = {
      related: doc?.adjustments?.length ?? 0,
      files: (doc?.attachments?.length ?? 0) + (doc?.order_attachments?.length ?? 0),
    }
    return REL_TABS.map((t) => (counts[t.key] ? { ...t, count: counts[t.key] } : t))
  }, [doc?.adjustments, doc?.attachments, doc?.order_attachments])

  // Vista inicial del centro. Borrador/rechazado SIEMPRE arranca en el form
  // (no hay PDF/XML emitido). Para el resto, respeta la preferencia del perfil;
  // si no hay preferencia, el default global es 'detail' (NO 'pdf'): el detalle
  // renderiza del doc ya cargado y es instantáneo, mientras el PDF tarda en
  // generarse/descargar. Quien prefiera PDF/XML lo fija con la estrella.
  useEffect(() => {
    if (!doc) return
    const editable = doc.status === 'draft' || doc.status === 'rejected'
    setCenterView(editable ? 'detail' : (prefView ?? 'detail'))
  }, [doc?.id, doc?.status, prefView])

  const setDefaultView = (v: CenterView) => {
    updatePref.mutate({ sales_360_default_view: v }, {
      onSuccess: () => toast.success(`Vista "${CENTER_LABEL[v]}" fijada como predeterminada.`),
      onError: () => toast.error('No se pudo guardar la preferencia.'),
    })
  }

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center text-content-muted">
        <Loader2 className="animate-spin" size={28} />
      </div>
    )
  }
  if (isError || !doc) {
    return (
      <div className="h-full grid place-items-center gap-3 text-content-muted">
        <p>No se pudo cargar el documento.</p>
        <button onClick={() => navigate(listPath)} className="text-primary font-semibold">
          ← Volver al listado
        </button>
      </div>
    )
  }

  const folio = doc.folio || (doc.localization_data?.document_number as string) || '—'
  const rut = doc.customer_name ? formatRut(String((doc as { customer_tax_id?: string }).customer_tax_id ?? '')) : ''

  return (
    <>
    <Workspace360
      rail={
        <DocsRail
          docs={listData?.documents ?? []}
          total={listData?.count ?? 0}
          loading={isFetchingList}
          onLoadMore={() => setRailSize((s) => s + 25)}
          activeId={doc.id}
          onBack={() => navigate(listPath)}
          onPick={(d) => navigate(`${listPath}/${d.id}/360`)}
          fmt={fmt}
        />
      }
      header={
        <DocHeader
          doc={doc} folio={folio} rut={rut} fmt={fmt}
          onBack={() => navigate(listPath)}
          can={can}
          onCustomer={() => setQuick({ type: 'customer', customerId: doc.customer })}
          liveTotals={liveTotals}
          isSaving={false}
          onEmit={handleEmit}
          onClone={handleClone}
          onEmail={() => setEmailOpen(true)}
          onPaymentLink={() => setPaymentLinkOpen(true)}
          onDistribute={handleDistribute}
          onAdjust={handleAdjust}
        />
      }
      chips={<ChipsStrip doc={doc} locale={locale} onVoucher={() => setQuick({ type: 'voucher', docId: doc.id })} />}
      center={
        <CenterPane
          doc={doc} view={centerView} onView={setCenterView} locale={locale} fmt={fmt}
          onProduct={(productId, name) => setQuick({ type: 'product', productId, name })}
          onTotalsChange={setLiveTotals}
          defaultView={prefView}
          onSetDefaultView={setDefaultView}
        />
      }
      panel={
        <div className="grid grid-cols-[1fr_48px] gap-2.5 items-start">
          <div className="bg-surface border border-divider rounded-xl shadow-sm min-h-[200px]">
            <div className="sticky top-[var(--head-h,130px)] z-[6] flex items-center gap-2 px-4 py-3 border-b border-divider font-extrabold text-sm bg-surface rounded-t-xl">
              {REL_TABS.find((t) => t.key === activeTab)?.title}
            </div>
            <div className="p-4">
              <PanelContent
                tab={activeTab}
                doc={doc}
                fmt={fmt}
                locale={locale}
                onNavigate={goDoc}
                onPayment={(payment) => setQuick({ type: 'payment', payment })}
                notify={toast}
                onOpenCustomer={() => setQuick({ type: 'customer', customerId: doc.customer })}
                onOpenVoucher={() => setQuick({ type: 'voucher', docId: doc.id })}
              />
            </div>
          </div>
          <div className="sticky top-[var(--head-h,130px)]">
            <RelatedTabsRail tabs={tabsWithCounts} activeKey={activeTab} onChange={setActiveTab} />
          </div>
        </div>
      }
    />

      {quick?.type === 'product' && (
        <ProductQuickView productId={quick.productId} productName={quick.name} onClose={() => setQuick(null)} fmt={fmt} locale={locale} onNavigate={goDoc} />
      )}
      {quick?.type === 'customer' && (
        <CustomerQuickView customerId={quick.customerId} onClose={() => setQuick(null)} fmt={fmt} locale={locale} onNavigate={goDoc} />
      )}
      {quick?.type === 'voucher' && (
        <VoucherQuickView docId={quick.docId} onClose={() => setQuick(null)} fmt={fmt} locale={locale} notify={toast} />
      )}
      {quick?.type === 'payment' && (
        <PaymentQuickView payment={quick.payment} onClose={() => setQuick(null)} fmt={fmt} locale={locale} notify={toast} />
      )}

      {emailOpen && (
        <SendEmailModal
          open={emailOpen}
          onClose={() => setEmailOpen(false)}
          documentId={doc.id}
          documentLabel={`${doc.document_type_display} N° ${folio}`}
        />
      )}
      {paymentLinkOpen && (
        <PaymentLinkModal docId={doc.id} onClose={() => setPaymentLinkOpen(false)} />
      )}

      {/* Ajuste NC/ND: el mismo modal del listado, montado sobre la 360. */}
      <DocumentModal
        open={adjustOpen}
        onClose={() => { setAdjustOpen(false); setAdjustTargetId(null) }}
        onSubmit={handleAdjustSubmit}
        isPending={adjustDocMut.isPending}
        initial={adjustInitial}
        customers={customersData?.customers || []}
        globalFields={globalFields}
        currencyRates={currencyRatesData?.rates}
      />
      <ConfirmModal {...confirmProps} />

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}

// ─── Rail izquierdo: lista de documentos ──────────────────────────────────────

interface DocsRailProps {
  docs: SaleDocumentListItem[]
  total: number
  loading: boolean
  onLoadMore: () => void
  activeId: string
  onBack: () => void
  onPick: (d: SaleDocumentListItem) => void
  fmt: (v: string | number) => string
}
function DocsRail({ docs, total, loading, onLoadMore, activeId, onBack, onPick, fmt }: DocsRailProps) {
  const canLoadMore = docs.length < total
  const activeRef = useRef<HTMLButtonElement>(null)
  // Mantiene visible el documento activo dentro del rail: al seleccionar uno que
  // está más abajo en la lista (o al entrar por deep-link), lo lleva al viewport
  // del propio contenedor sin mover el resto de la página (`block: 'nearest'`).
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' })
  }, [activeId, docs])

  return (
    <>
      <button
        onClick={onBack}
        title="Volver al listado de documentos"
        className="flex items-center gap-2 px-3.5 py-3 border-b border-divider font-bold text-[13px] w-full text-left text-content-muted hover:bg-surface-muted hover:text-content"
      >
        <ChevronLeft size={16} />
        <span className="flex items-center gap-2"><FileText size={15} /> Documentos</span>
      </button>
      <div className="overflow-y-auto scrollbar-slim p-2 flex-1">
        {docs.map((d) => {
          const active = d.id === activeId
          const fol = d.folio || '—'
          return (
            <button
              key={d.id}
              ref={active ? activeRef : undefined}
              onClick={() => onPick(d)}
              className={`block w-full text-left rounded-xl border p-2.5 mb-1.5 ${active ? 'bg-primary/10 border-primary' : 'border-transparent hover:bg-surface-muted'}`}
            >
              <div className="flex justify-between gap-2 text-[12.5px]">
                <span className="font-bold">{d.document_type} {fol}</span>
                <span className="text-content-subtle">{fmtDate(d.issue_date, 'es-CL')}</span>
              </div>
              <div className="text-content-muted text-[12px] my-0.5 truncate">{d.customer_name}</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] font-bold tabular-nums">{fmt(d.total)}</span>
                <StatusBadge status={d.status} className="shrink-0 text-[10px]" />
              </div>
            </button>
          )
        })}
        {canLoadMore && (
          <button
            onClick={onLoadMore}
            disabled={loading}
            className="block w-full text-center rounded-xl border border-divider px-3 py-2 mt-1 text-[12px] font-semibold text-content-muted hover:bg-surface-muted hover:text-content disabled:opacity-60"
          >
            {loading ? 'Cargando…' : `Cargar más documentos (${docs.length} de ${total})`}
          </button>
        )}
      </div>
    </>
  )
}

// ─── Header del documento ──────────────────────────────────────────────────────

interface DocHeaderProps {
  doc: SaleDocument
  folio: string
  rut: string
  fmt: (v: string | number) => string
  onBack: () => void
  can: (m: string, a: string) => boolean
  onCustomer: () => void
  /** Totales en vivo del formulario (editables): el header pasa a ser el resumen. */
  liveTotals?: DocumentFormTotals | null
  isSaving?: boolean
  onEmit: () => void
  onClone: () => void
  onEmail: () => void
  onPaymentLink: () => void
  onDistribute: () => void
  onAdjust: () => void
}
function DocHeader({ doc, folio, rut, fmt, onBack, can, onCustomer, liveTotals, isSaving, onEmit, onClone, onEmail, onPaymentLink, onDistribute, onAdjust }: DocHeaderProps) {
  const locale = useTenantLocale()
  const tax = useTenantTax()
  const isEditable = doc.status === 'draft' || doc.status === 'rejected'
  // Una Guía de Despacho (GD) no se cobra ni se ajusta con NC/ND (no reconoce ingreso).
  const isGuide = doc.document_type === 'GD'
  const listPath = isGuide ? '/sales/guides' : '/sales/documents'
  const stats = useMemo(() => {
    if (isEditable && liveTotals) {
      const s = [
        { label: 'Neto', value: fmt(liveTotals.neto) },
        ...(liveTotals.exento > 0 ? [{ label: 'Exento', value: fmt(liveTotals.exento) }] : []),
        { label: tax.name, value: fmt(liveTotals.iva) },
        ...(liveTotals.foreignCurrency && liveTotals.rate
          ? [{ label: `Neto ${liveTotals.foreignCurrency}`, value: `${liveTotals.foreignCurrency} ${(liveTotals.neto / liveTotals.rate).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, variant: 'fx' as const, sub: `1 ${liveTotals.foreignCurrency} = ${formatExchangeRate(liveTotals.rate, locale)}` }]
          : []),
        { label: 'Total', value: fmt(liveTotals.total), variant: 'main' as const },
      ]
      return s
    }
    return buildDocMoneyStats({ ...doc, outstanding_amount: doc.outstanding }, fmt)
  }, [isEditable, liveTotals, doc, fmt, locale])
  const canEdit = can('sales.documents', 'edit')
  const canCreate = can('sales.documents', 'create')

  return (
    <div className="px-[18px] py-3.5">
      <div className="flex items-start gap-3.5">
        <button onClick={onBack} title="Volver" className="w-9 h-9 rounded-lg border border-divider grid place-items-center text-content-muted hover:bg-surface-muted shrink-0">
          <ArrowLeft size={17} />
        </button>
        <div className="w-[46px] h-[46px] rounded-xl grid place-items-center shrink-0 bg-soft text-primary">
          <FileText size={22} />
        </div>
        <div className="min-w-0">
          <div className="text-xl font-extrabold -tracking-[0.3px] flex items-center gap-2.5 flex-wrap">
            {doc.document_type_display} <span className="text-content-muted">N° {folio}</span>
            <StatusBadge status={doc.status} />
          </div>
          <button onClick={onCustomer} className="text-content-muted text-[13px] mt-0.5 hover:text-primary hover:underline text-left" title="Ver cliente">
            {doc.customer_name}{rut ? ` · ${rut}` : ''}
          </button>
        </div>
        <div className="ml-auto">
          <MoneyTotals stats={stats} />
        </div>
      </div>

      <div className="flex gap-2 mt-3.5 flex-wrap">
        {isEditable && canEdit && (
          <HeaderBtn primary type="submit" form="dte-form" icon={<Save size={15} />} label={isSaving ? 'Guardando…' : 'Guardar'} />
        )}
        {isEditable && canEdit && (
          <HeaderBtn icon={<Send size={15} />} label={doc.status === 'rejected' ? 'Reintentar' : 'Emitir'} onClick={onEmit} />
        )}
        {!isEditable && (
          <>
            {!isGuide && doc.status === 'accepted' && canCreate && (
              <HeaderBtn icon={<Link2 size={15} />} label="Ajuste NC/ND" onClick={onAdjust} />
            )}
            <HeaderBtn icon={<Mail size={15} />} label="Distribuir" onClick={onDistribute} />
            <HeaderBtn icon={<Send size={15} />} label="Enviar correo" onClick={onEmail} />
            {!isGuide && (
              <HeaderBtn icon={<DollarSign size={15} />} label="Link de pago" onClick={onPaymentLink} />
            )}
          </>
        )}
        <HeaderBtn icon={<Copy size={15} />} label="Clonar" onClick={onClone} />
        <HeaderBtn icon={<Download size={15} />} label="PDF" onClick={() => openPdf(doc.id)} />
        <CopyLinkButton docId={doc.id} basePath={listPath} />
        {isEditable && (
          <HeaderBtn icon={<ArrowLeft size={15} />} label="Cancelar" onClick={onBack} />
        )}
      </div>
    </div>
  )
}

function CopyLinkButton({ docId, basePath = '/sales/documents' }: { docId: string; basePath?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <HeaderBtn
      icon={copied ? <Check size={15} className="text-success" /> : <Copy size={15} />}
      label={copied ? 'Copiado' : 'Copiar enlace'}
      onClick={() => {
        navigator.clipboard?.writeText(`${window.location.origin}${basePath}/${docId}/360`)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
    />
  )
}

async function openPdf(id: string) {
  try {
    const res = await apiClient.get(`/sales/documents/${id}/pdf/`, { responseType: 'blob' })
    window.open(URL.createObjectURL(res.data), '_blank')
  } catch { /* ignore */ }
}

function HeaderBtn({ label, icon, primary, onClick, type = 'button', form }: { label: string; icon: React.ReactNode; primary?: boolean; onClick?: () => void; type?: 'button' | 'submit'; form?: string }) {
  return (
    <button
      type={type}
      form={form}
      onClick={onClick}
      className={[
        'rounded-lg px-3.5 py-2 text-[13px] font-semibold inline-flex items-center gap-1.5 border',
        primary
          ? 'bg-primary border-primary text-on-primary hover:bg-primary-hover'
          : 'bg-surface border-divider-strong text-content hover:bg-surface-muted',
      ].join(' ')}
    >
      {icon}{label}
    </button>
  )
}

// ─── Chips de estado ──────────────────────────────────────────────────────────

function ChipsStrip({ doc, locale, onVoucher }: { doc: SaleDocument; locale: string; onVoucher: () => void }) {
  const ld = doc.localization_data || {}
  const trackid = (ld.track_id as string) || (ld.trackid as string) || '—'
  const chips = [
    { lbl: 'Vence', val: doc.due_date ? fmtDate(doc.due_date, locale) : '—' },
    { lbl: 'Forma de pago', val: doc.payment_method === 'credito' ? `Crédito ${doc.payment_term_days ?? 0} días` : 'Contado' },
    { lbl: 'TrackID', val: trackid },
    { lbl: 'Cesión', val: (doc as { factoring_status?: string }).factoring_status === 'ceded' ? 'Cedido' : 'No cedido' },
    { lbl: 'Contabilidad', val: doc.accounting_status === 'accounted' ? 'Contabilizado' : 'Pendiente', onClick: onVoucher },
    { lbl: 'Cobranza', val: collectionLabel(doc.collection_status) },
  ]
  return (
    <div className="grid grid-cols-6 gap-2.5 max-[860px]:grid-cols-3 max-[600px]:grid-cols-2">
      {chips.map((c) => {
        const Tag = c.onClick ? 'button' : 'div'
        return (
          <Tag
            key={c.lbl}
            onClick={c.onClick}
            className={`bg-surface border border-divider rounded-xl px-3 py-2.5 shadow-sm text-left ${c.onClick ? 'hover:border-primary hover:bg-primary/5 cursor-pointer' : ''}`}
          >
            <div className="text-[10.5px] uppercase tracking-wide text-content-subtle">{c.lbl}</div>
            <div className="text-[13px] font-bold mt-1 truncate" title={c.val}>{c.val}</div>
          </Tag>
        )
      })}
    </div>
  )
}
function collectionLabel(s?: string): string {
  return { pending: 'Pendiente', partial: 'Parcial', paid: 'Pagada', overdue: 'Vencida', '': '—' }[s ?? ''] ?? (s ?? '—')
}

// ─── Centro: PDF / Detalle / XML ───────────────────────────────────────────────

interface CenterPaneProps {
  doc: SaleDocument
  view: CenterView
  onView: (v: CenterView) => void
  locale: string
  fmt: (v: string | number) => string
  onProduct: (productId: string, name: string) => void
  onTotalsChange: (t: DocumentFormTotals) => void
  /** Vista predeterminada del perfil (para marcar la estrella). */
  defaultView?: CenterView
  /** Fija la vista actual como predeterminada (a nivel de perfil). */
  onSetDefaultView?: (v: CenterView) => void
}
function CenterPane({ doc, view, onView, locale, fmt, onProduct, onTotalsChange, defaultView, onSetDefaultView }: CenterPaneProps) {
  // Borrador/rechazado son editables: el centro "Detalle" muestra el formulario.
  const isEditable = doc.status === 'draft' || doc.status === 'rejected'
  const detailLabel = isEditable ? 'Detalle / Edición' : 'Detalle'
  const isDefault = defaultView === view
  return (
    <div className="bg-surface border border-divider rounded-xl shadow-sm flex flex-col">
      <div className="sticky top-[var(--head-h,130px)] z-[6] flex items-center gap-2.5 px-3 py-2.5 border-b border-divider bg-surface rounded-t-xl">
        <div className="inline-flex bg-soft rounded-lg p-0.5">
          {([['pdf', 'PDF', <Download size={14} key="p" />], ['detail', detailLabel, <FilePenLine size={14} key="d" />], ['xml', 'XML', <FileCode2 size={14} key="x" />]] as const).map(
            ([k, label, ic]) => (
              <button
                key={k}
                onClick={() => onView(k)}
                className={[
                  'px-3.5 py-1.5 rounded-md text-[12.5px] font-semibold inline-flex gap-1.5 items-center',
                  view === k ? 'bg-surface text-content shadow-sm' : 'text-content-muted',
                ].join(' ')}
              >
                {ic}{label}
              </button>
            ),
          )}
        </div>
        {/* Fijar la vista actual como predeterminada (persiste en el perfil). */}
        {onSetDefaultView && (
          <button
            type="button"
            onClick={() => !isDefault && onSetDefaultView(view)}
            title={isDefault ? 'Esta es tu vista predeterminada' : `Fijar "${CENTER_LABEL[view]}" como vista predeterminada`}
            aria-pressed={isDefault}
            className={`ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11.5px] font-medium border transition-colors ${
              isDefault
                ? 'border-primary/40 text-primary bg-primary/5 cursor-default'
                : 'border-divider text-content-muted hover:bg-soft hover:text-content'
            }`}
          >
            <Star size={13} className={isDefault ? 'fill-primary text-primary' : ''} />
            {isDefault ? 'Predeterminada' : 'Fijar como predeterminada'}
          </button>
        )}
      </div>
      <div className={view === 'detail' && isEditable ? '' : 'p-[18px]'}>
        {view === 'pdf' && <PdfView docId={doc.id} />}
        {view === 'detail' && (isEditable
          ? <DocumentFormPage embedded onTotalsChange={onTotalsChange} />
          : <DetailView doc={doc} locale={locale} fmt={fmt} onProduct={onProduct} />)}
        {view === 'xml' && <XmlView docId={doc.id} />}
      </div>
    </div>
  )
}

function PdfView({ docId }: { docId: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)
  useEffect(() => {
    let revoked: string | null = null
    setError(false)
    setUrl(null)
    apiClient
      .get(`/sales/documents/${docId}/pdf/`, { responseType: 'blob' })
      .then((res) => {
        const u = URL.createObjectURL(res.data)
        revoked = u
        setUrl(u)
      })
      .catch(() => setError(true))
    return () => {
      if (revoked) URL.revokeObjectURL(revoked)
    }
  }, [docId])

  if (error) return <p className="text-content-muted text-center py-10">No hay PDF disponible para este documento.</p>
  if (!url) return <div className="grid place-items-center py-16 text-content-muted"><Loader2 className="animate-spin" /></div>
  return <iframe title="PDF" src={url} className="w-full h-[calc(100vh-180px)] min-h-[600px] rounded-lg border border-divider bg-white" />
}

function XmlView({ docId }: { docId: string }) {
  const [content, setContent] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [copied, setCopied] = useState(false)

  // Siempre el XML firmado (con fallback a sin firmar si aún no está firmado).
  useEffect(() => {
    let cancel = false
    setContent(null); setError(false)
    apiClient.get(`/sales/documents/${docId}/xml/?variant=signed`, { responseType: 'text' })
      .then(res => { if (!cancel) setContent(typeof res.data === 'string' ? res.data : JSON.stringify(res.data)) })
      .catch(() => apiClient.get(`/sales/documents/${docId}/xml/?variant=unsigned`, { responseType: 'text' })
        .then(res => { if (!cancel) setContent(typeof res.data === 'string' ? res.data : JSON.stringify(res.data)) })
        .catch(() => { if (!cancel) setError(true) }))
    return () => { cancel = true }
  }, [docId])

  const download = () => {
    if (!content) return
    const blob = new Blob([content], { type: 'application/xml' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = `${docId}.xml`; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  const copy = () => { if (content) { navigator.clipboard?.writeText(content); setCopied(true); setTimeout(() => setCopied(false), 2000) } }

  if (error) return <p className="text-content-muted text-center py-10">Este documento no tiene XML disponible.</p>
  if (content === null) return <div className="grid place-items-center py-16 text-content-muted"><Loader2 className="animate-spin" /></div>

  return (
    <div className="rounded-xl border border-divider overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-surface-muted border-b border-divider">
        <span className="text-xs font-medium text-content-muted">XML firmado <span className="text-content-subtle">({Math.round(content.length / 1024)} KB)</span></span>
        <div className="flex items-center gap-1">
          <button onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted hover:text-content hover:bg-surface rounded">
            {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}{copied ? 'Copiado' : 'Copiar'}
          </button>
          <button onClick={download} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted hover:text-content hover:bg-surface rounded">
            <Download size={12} /> Descargar
          </button>
        </div>
      </div>
      <pre className="p-3 text-[11px] leading-relaxed font-mono text-content-muted overflow-auto scrollbar-slim max-h-[70vh] whitespace-pre bg-surface-muted">{formatXml(content)}</pre>
    </div>
  )
}

const SecHead = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[13px] font-extrabold uppercase tracking-wide text-content-subtle border-b border-divider pb-2 mb-3">{children}</p>
)

function DetailView({ doc, locale, fmt, onProduct }: { doc: SaleDocument; locale: string; fmt: (v: string | number) => string; onProduct: (productId: string, name: string) => void }) {
  const navigate = useNavigate()
  const tax = useTenantTax()
  const { data: customer } = useCustomer(doc.customer)
  // Mapa SKU→id para abrir la ficha del producto al clickear una línea aunque
  // la línea no traiga product_id (docs antiguos / importados / seed).
  const { data: productsData } = useProducts({ active: 'true', page_size: 9999 })
  const skuToId = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of productsData?.products ?? []) if (p.sku) m.set(p.sku, p.id)
    return m
  }, [productsData])
  const resolveProductId = (l: { product_id?: string | null; sku?: string | null }) =>
    l.product_id || (l.sku ? skuToId.get(l.sku) : undefined)
  const num3 = (v: string | number) =>
    new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(v) || 0)
  const fxEntries = Object.entries(doc.exchange_rate_data || {}).filter(
    ([k]) => !['locked_at', 'is_locked', 'lock_source'].includes(k),
  )
  const sellerName = (doc as { seller_name?: string | null }).seller_name

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <div>
        <SecHead>Encabezado</SecHead>
        <div className="grid grid-cols-3 gap-3.5 max-[860px]:grid-cols-2 max-[600px]:grid-cols-1">
          <Field label="Tipo de documento" value={doc.document_type_display} />
          <Field label="Estado" value={doc.status_display} />
          <Field label="Fecha de emisión" value={fmtDate(doc.issue_date, locale)} />
          <Field label="Fecha de vencimiento" value={doc.due_date ? fmtDate(doc.due_date, locale) : '—'} />
          <Field label="Forma de pago" value={doc.payment_method === 'credito' ? `Crédito (${doc.payment_term_days ?? 0} días)` : 'Contado'} />
          <Field label="Folio" value={doc.folio || '—'} />
          {sellerName && <Field label="Vendedor" value={sellerName} />}
          {doc.sales_order_number && <Field label="Pedido de origen" value={doc.sales_order_number} />}
          {doc.contract && (
            <Field label="Contrato" value={
              <button
                type="button"
                onClick={() => navigate(`/sales/contracts?search=${encodeURIComponent(doc.contract_number || '')}`)}
                className="text-primary hover:underline text-left"
                title="Ver contrato"
              >
                {doc.contract_number || 'Ver contrato'}{doc.contract_title ? ` — ${doc.contract_title}` : ''}
              </button>
            } />
          )}
        </div>
      </div>

      {/* Receptor */}
      <div>
        <SecHead>Receptor</SecHead>
        <div className="grid grid-cols-3 gap-3.5 max-[860px]:grid-cols-2 max-[600px]:grid-cols-1">
          <Field label="RUT / ID Tributario" value={customer?.tax_id ? formatRut(customer.tax_id) : '—'} />
          <Field label="Razón Social" value={customer?.legal_name || doc.customer_name} />
          <Field label="Giro Comercial" value={customer?.giro || '—'} />
          <Field label="Dirección" value={customer?.address_street || '—'} />
          <Field label="Comuna" value={customer?.address_commune || '—'} />
          <Field label="Ciudad" value={customer?.address_city || '—'} />
        </div>
      </div>

      {/* Líneas */}
      <div>
        <SecHead>Líneas del documento</SecHead>
        <DataTable<NonNullable<typeof doc.lines>[number]>
          columns={[
            { key: 'sku', header: 'SKU',
              render: l => <span className="font-mono text-content-muted text-xs">{l.sku || '—'}</span> },
            { key: 'name', header: 'Nombre',
              render: l => <span className="font-medium">{l.name}</span> },
            { key: 'description', header: 'Descripción',
              render: l => <span className="text-content-muted text-xs">{l.description || '—'}</span> },
            { key: 'quantity', header: 'Cant.', numeric: true,
              render: l => num3(l.quantity) },
            { key: 'unit_price', header: 'P. Unit.', numeric: true,
              render: l => (
                l.unit_price_original && l.price_currency && l.price_currency !== 'CLP' ? (
                  <div className="space-y-0.5">
                    <div className="text-[11px] font-bold text-primary">
                      {l.price_currency} {l.unit_price_original}
                    </div>
                    <div>{fmt(l.unit_price)}</div>
                  </div>
                ) : fmt(l.unit_price)
              ) },
            { key: 'discount_pct', header: 'Desc %', numeric: true,
              render: l => (
                <span className="text-content-muted">
                  {l.discount_pct && parseFloat(l.discount_pct) > 0 ? `${l.discount_pct}%` : '—'}
                </span>
              ) },
            { key: 'subtotal', header: 'Subtotal', numeric: true,
              render: l => <span className="font-semibold">{l.subtotal ? fmt(l.subtotal) : '—'}</span> },
          ]}
          rows={doc.lines ?? []}
          rowKey={(_, i) => i}
          minWidth={900}
          // Solo las líneas CON producto llevan al detalle: sin esto, las demás
          // quedarían con cursor de mano prometiendo una ficha que no existe.
          rowClickable={l => !!resolveProductId(l)}
          onRowClick={l => {
            const pid = resolveProductId(l)
            if (pid) onProduct(pid, l.name)
          }}
          caption="Líneas del documento"
        />

        {/* Tipos de cambio */}
        {fxEntries.length > 0 && (
          <div className="mt-2 flex justify-end">
            <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 text-xs space-y-1">
              <p className="font-semibold text-primary">Tipos de cambio {String(doc.exchange_rate_data?.is_locked) === 'true' ? '(bloqueados al emitir)' : '(indicativos)'}</p>
              {fxEntries.map(([code, rate]) => {
                const r = Number(rate)
                return (
                  <div key={code} className="text-primary">
                    <div>1 {code} = {formatExchangeRate(rate, locale)}</div>
                    {r > 0 && Number(doc.total) > 0 && (
                      <div className="font-semibold">Total: {code} {(Number(doc.total) / r).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Totales */}
        <div className="mt-3 flex justify-end gap-6 text-sm">
          <div className="text-right"><p className="text-xs text-content-subtle">Subtotal</p><p className="font-medium tabular-nums">{fmt(doc.subtotal)}</p></div>
          <div className="text-right"><p className="text-xs text-content-subtle">{tax.name} ({doc.tax_rate}%)</p><p className="font-medium tabular-nums">{fmt(doc.tax_amount)}</p></div>
          {doc.document_type === 'FC' && (
            <div className="text-right"><p className="text-xs text-warning">{tax.name} Retenido</p><p className="font-medium tabular-nums text-warning">-{fmt(doc.tax_amount)}</p></div>
          )}
          <div className="text-right"><p className="text-xs text-content-subtle">Total</p><p className="text-lg font-bold tabular-nums">{fmt(doc.total)}</p></div>
        </div>
      </div>

      {/* Referencias */}
      {doc.referencias && doc.referencias.length > 0 && (
        <div>
          <SecHead>Referencias</SecHead>
          <DataTable<typeof doc.referencias[number]>
            columns={[
              { key: 'tipo_doc', header: 'Tipo Doc',
                render: ref => <span className="font-mono">{ref.tipo_doc}</span> },
              { key: 'folio_ref', header: 'Folio Ref',
                render: ref => <>{ref.folio_ref}</> },
              { key: 'fecha_ref', header: 'Fecha Ref',
                render: ref => (
                  <span className="text-content-muted">
                    {ref.fecha_ref ? fmtDate(ref.fecha_ref, locale) : '—'}
                  </span>
                ) },
              { key: 'cod_ref', header: 'Código',
                render: ref => <span className="text-content-muted">{ref.cod_ref || '—'}</span> },
              { key: 'razon_ref', header: 'Razón',
                render: ref => <span className="text-content-muted">{ref.razon_ref || '—'}</span> },
            ]}
            rows={doc.referencias}
            rowKey={(_, i) => i}
            minWidth={620}
            caption="Documentos a los que este hace referencia"
          />
        </div>
      )}

      {/* Notas y campos personalizados (editable, no contable — cualquier estado) */}
      <MetaSection doc={doc} />
    </div>
  )
}

/**
 * Notas y campos personalizados — editable en cualquier estado del documento.
 * Reusa PATCH /sales/documents/:id/metadata/ (no toca contabilidad ni el DTE),
 * mismo flujo que el modal de detalle clásico (capacidad agregada en #462).
 */
function MetaSection({ doc }: { doc: SaleDocument }) {
  const updateMeta = useUpdateDocumentMetadata(doc.id)
  // Los campos que se muestran salen de las DEFINICIONES del tenant, no del JSON
  // del documento: el JSON solo guarda lo que se llenó, así que los documentos
  // anteriores a un campo (o con el campo vacío) no lo traían y no había dónde
  // escribirlo. Medido en prod: 3.356 de 3.524 documentos sin "Liquidada".
  const { data: ventasFields = [] } = useGlobalCustomFields(true, 'ventas')
  const [editing, setEditing] = useState(false)
  const [draftNotes, setDraftNotes] = useState(doc.notes ?? '')
  const [draftPers, setDraftPers] = useState<DocumentPersonalizado[]>(
    () => mergeCustomFields(ventasFields, doc.personalizados) as DocumentPersonalizado[],
  )

  const startEdit = () => {
    setDraftNotes(doc.notes ?? '')
    setDraftPers(mergeCustomFields(ventasFields, doc.personalizados) as DocumentPersonalizado[])
    setEditing(true)
  }

  const visibles = mergeCustomFields(ventasFields, doc.personalizados) as DocumentPersonalizado[]
  const hasContent = visibles.length > 0 || !!doc.notes
  // Campos guardados que ya no están definidos: se editan como texto libre.
  const nombresDefinidos = new Set(ventasFields.map(f => f.nombre))
  const extras = draftPers.filter(p => !nombresDefinidos.has(p.nombre))

  return (
    <div>
      <div className="flex items-center justify-between border-b border-divider pb-2 mb-3">
        <p className="text-[13px] font-extrabold uppercase tracking-wide text-content-subtle">Notas y campos personalizados</p>
        {doc.status !== 'cancelled' && !editing && (
          <button onClick={startEdit} className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary-hover font-semibold">
            <Pencil size={12} />Editar
          </button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-3">
          {visibles.length > 0 && (
            <div className="grid grid-cols-3 gap-3.5 max-[860px]:grid-cols-2 max-[600px]:grid-cols-1">
              {visibles.map((p, i) => (
                <Field key={p.nombre || i} label={p.nombre || `Campo ${i + 1}`} value={p.tipo === 'boolean' && p.valor ? (p.valor === 'true' ? 'Sí' : 'No') : (p.valor_label || p.valor || '—')} />
              ))}
            </div>
          )}
          {doc.notes ? (
            <p className="text-[13px] text-content-muted whitespace-pre-wrap">{doc.notes}</p>
          ) : !hasContent && (
            <p className="text-xs text-content-subtle">Sin notas ni campos personalizados.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {/* Los definidos por el tenant se editan con el control que les
              corresponde (select con sus opciones, fecha, vínculo a entidad), no
              como texto libre: antes un "Tipo Negocio" con 3 opciones se tipeaba
              a mano y admitía cualquier cosa. */}
          <CustomFieldsForm
            fields={ventasFields}
            value={draftPers}
            onChange={next => setDraftPers(next as DocumentPersonalizado[])}
          />
          {extras.length > 0 && (
            <div className="space-y-2">
              {extras.map(p => (
                <div key={p.nombre} className="grid grid-cols-3 gap-2 items-center">
                  <span className="text-[13px] text-content-muted truncate" title={p.nombre}>{p.nombre}</span>
                  <input aria-label={p.nombre}
                    value={p.valor ?? ''}
                    onChange={e => setDraftPers(arr => arr.map(x => x.nombre === p.nombre ? { ...x, valor: e.target.value } : x))}
                    className="col-span-2 px-2 py-1.5 text-[13px] border border-divider rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30"
                    placeholder={`Valor de ${p.nombre}`}
                  />
                </div>
              ))}
            </div>
          )}
          <div>
            <label htmlFor="document360page-observaciones" className="text-[11.5px] font-semibold text-content-muted">Observaciones</label>
            <textarea id="document360page-observaciones"
              value={draftNotes}
              onChange={e => setDraftNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full px-3 py-2 text-[13px] border border-divider rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Notas u observaciones internas…"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-xs border border-divider rounded-lg text-content-muted hover:bg-soft">Cancelar</button>
            <button
              onClick={() => updateMeta.mutate(
                { personalizados: draftPers, notes: draftNotes },
                { onSuccess: () => setEditing(false) },
              )}
              disabled={updateMeta.isPending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50"
            >
              {updateMeta.isPending && <Loader2 size={12} className="animate-spin" />}Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</span>
      <span className="text-[13.5px] text-content break-words">{value}</span>
    </div>
  )
}

// ─── Panel contextual (tabs) ───────────────────────────────────────────────────

function PanelContent({ tab, doc, fmt, locale, onNavigate, onPayment, notify, onOpenCustomer, onOpenVoucher }: {
  tab: string
  doc: SaleDocument
  fmt: (v: string | number) => string
  locale: string
  onNavigate: (docId: string) => void
  onPayment: (payment: CollectionResponse['payments'][number]) => void
  notify: { success: (m: string) => void; error: (m: string) => void }
  onOpenCustomer: () => void
  onOpenVoucher: () => void
}) {
  switch (tab) {
    case 'customer': return <CustomerContextTab customerId={doc.customer} fmt={fmt} locale={locale} onNavigate={onNavigate} onViewCustomer={onOpenCustomer} />
    case 'sii': return <SiiTimelineTab doc={doc} locale={locale} />
    case 'related': return <RelatedDocsTab doc={doc} fmt={fmt} locale={locale} onNavigate={onNavigate} />
    case 'voucher': return <VoucherTab doc={doc} fmt={fmt} locale={locale} notify={notify} onOpenVoucher={onOpenVoucher} />
    case 'collection': return <CollectionTab doc={doc} fmt={fmt} locale={locale} onPayment={onPayment} />
    case 'factoring': return <FactoringTab doc={doc} fmt={fmt} locale={locale} />
    case 'files': return <AttachmentsTab doc={doc} locale={locale} />
    case 'history': return <HistoryTab doc={doc} locale={locale} />
    default: return null
  }
}
